/**
 * One catalog entry, with the two edits that are safe to make from a list
 * view: rename, and activate or disable.
 *
 * Deliberately not a full editor yet. A bundle's entries and a trigger's
 * conditions are where the expensive mistakes live — a bundle pointing at a
 * tag no active reward carries saves clean and gives out nothing, on stream —
 * and they deserve a purpose-built form rather than a JSON box that technically
 * works. Until then this shows everything and edits only what cannot be got
 * wrong.
 *
 * What it does prove: the PATCH contract. The console sends only what changed,
 * the server merges it onto the current resource and revalidates the WHOLE
 * thing against the same schema create uses (L16).
 */
import { useState, type ReactNode } from 'react';
import type { Bundle, Reward, Trigger } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Badge, Button, Failed, Link, Spinner } from '../components/primitives.jsx';
import { ApiError } from '../lib/api.js';
import { hrefFor } from '../lib/router.js';
import { bundles, rewards, triggers, type CatalogKind } from '../lib/tomo.js';
import { useAsync } from '../useAsync.js';
import { useSession } from '../Session.jsx';

type Entry = Reward | Bundle | Trigger;

export function CatalogEntry(
  { channelId, kind, id }: { channelId: string; kind: CatalogKind; id: string }
): ReactNode {
  const session = useSession();
  const channel = session.channels.find((c) => c.id === channelId);
  // Shown or hidden as a courtesy. The server checks the role again on every
  // request, so a console that only hid the button would be one anybody could
  // bypass with fetch.
  const canEdit = channel?.scopes.includes('rewards:write') ?? false;

  const [result, retry] = useAsync(
    (signal) => getterFor(kind)(channelId, id, signal),
    [channelId, kind, id]
  );

  if (result.state === 'loading') {
    return <Shell channelId={channelId}><Spinner label="Loading…" /></Shell>;
  }
  if (result.state === 'failed') {
    return (
      <Shell channelId={channelId}>
        <Failed
          message={result.error.status === 404 ? 'There is nothing here.' : 'Could not load it.'}
          detail={result.error.status === 404 ? undefined : result.error.message}
          {...(result.error.status === 404 ? {} : { onRetry: retry })}
        />
      </Shell>
    );
  }

  return (
    <Shell channelId={channelId}>
      <Detail
        channelId={channelId}
        kind={kind}
        entry={result.value}
        canEdit={canEdit}
        onSaved={retry}
      />
    </Shell>
  );
}

function Detail(
  { channelId, kind, entry, canEdit, onSaved }:
  {
    channelId: string; kind: CatalogKind; entry: Entry;
    canEdit: boolean; onSaved: () => void;
  }
): ReactNode {
  const [name, setName] = useState(entry.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const save = (patch: Record<string, unknown>): void => {
    setBusy(true);
    setError(null);
    // Only what changed. The server merges it onto the current resource and
    // revalidates the whole thing, so a partial body is both correct and the
    // smaller request.
    updaterFor(kind)(channelId, entry.id, patch).then(
      () => { setBusy(false); onSaved(); },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof ApiError
          ? err
          : new ApiError('malformed_response', 0, 'Something unexpected went wrong.'));
      }
    );
  };

  const active = 'active' in entry ? entry.active : entry.enabled;
  const activeField = 'active' in entry ? 'active' : 'enabled';

  return (
    <div>
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold tracking-tight">{entry.name}</h1>
          <p className="mt-1 font-mono text-xs text-ink-700">{entry.id}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {active ? <Badge tone="good">Live</Badge> : <Badge tone="warn">
            {activeField === 'active' ? 'Inactive' : 'Disabled'}
          </Badge>}
        </div>
      </div>

      {error ? (
        <div className="mt-6">
          <Failed
            message={error.message}
            detail={error.fields.map((f) => `${f.path}: ${f.message}`).join(' · ') || undefined}
          />
        </div>
      ) : null}

      {canEdit ? (
        <form
          className="mt-8 flex flex-wrap items-end gap-3"
          onSubmit={(e) => { e.preventDefault(); save({ name }); }}
        >
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-ink-400">Name</span>
            <input
              value={name}
              onChange={(e) => { setName(e.target.value); }}
              className="w-72 rounded-md border border-ink-800 bg-ink-900 px-3 py-2 text-ink-50"
            />
          </label>
          <Button type="submit" disabled={busy || name === entry.name}>
            {busy ? 'Saving…' : 'Rename'}
          </Button>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => { save({ [activeField]: !active }); }}
          >
            {active
              ? (activeField === 'active' ? 'Deactivate' : 'Disable')
              : (activeField === 'active' ? 'Activate' : 'Enable')}
          </Button>
        </form>
      ) : (
        <p className="mt-8 text-sm text-ink-400">
          Your role on this channel can view the catalog but not change it.
        </p>
      )}

      <div className="mt-10 space-y-8">
        {kind === 'rewards' ? <RewardDetail reward={entry as Reward} /> : null}
        {kind === 'bundles' ? <BundleDetail bundle={entry as Bundle} /> : null}
        {kind === 'triggers' ? <TriggerDetail trigger={entry as Trigger} /> : null}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section>
      <h2 className="text-sm font-medium uppercase tracking-wide text-ink-400">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function RewardDetail({ reward }: { reward: Reward }): ReactNode {
  const { stock } = reward;
  return (
    <>
      <Section title="Print run">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="brand">{axisLabel(reward.supply)}</Badge>
          {reward.supply.axis === 'unlimited' ? null : (
            <Badge>{reward.editions} edition numbers</Badge>
          )}
        </div>
        <p className="mt-2 text-sm">
          {stock.capacity === null
            ? `${String(stock.minted)} minted, unlimited.`
            : `${stock.minted.toLocaleString()} of ${stock.capacity.toLocaleString()} minted · ${(stock.remaining ?? 0).toLocaleString()} left`}
        </p>
        {/* Summed across every counter this reward owns, never stored. There is
            no single limit to show once a print sheet holds one counter per
            (item, tier) — and a stored total is the second home for a number
            that made the API report `issued: 0` after the hundredth copy. */}
        <p className="mt-1 text-xs text-ink-400">
          Across {stock.pool_size} {stock.pool_size === 1 ? 'item' : 'items'} in the pool.
        </p>
        {stock.minted > 0 ? (
          // Worth saying on the screen rather than only in a 400: an instance
          // stamped "#3 of 50" is a receipt, and moving the denominator
          // afterwards would make the collection hold two different claims
          // about one run.
          <p className="mt-2 text-xs text-ink-400">
            Fixed — copies of this exist, and each is stamped with its number.
          </p>
        ) : null}
      </Section>

      <Section title="Pool">
        {reward.pool.mode === 'tag' ? (
          <>
            <Badge tone="brand">#{reward.pool.tag}</Badge>
            <p className="mt-2 text-xs text-ink-400">
              A live query. Adding an item with this tag adds capacity; it never
              changes what anything else has left.
            </p>
          </>
        ) : (
          <p className="text-sm text-ink-400">
            {reward.pool.item_ids.length} named{' '}
            {reward.pool.item_ids.length === 1 ? 'item' : 'items'}
          </p>
        )}
      </Section>

      <Section title="Rarity">
        {reward.rarity.mode === 'none' ? (
          <p className="text-sm text-ink-400">Copies carry no tier.</p>
        ) : reward.rarity.mode === 'rolled' ? (
          <>
            <p className="text-sm text-ink-400">Rolled per copy.</p>
            {Object.keys(reward.rarity.weights).length > 0 ? (
              <ul className="mt-2 space-y-1">
                {shares(reward.rarity.weights).map(([key, pct]) => (
                  <li key={key} className="flex gap-3 text-sm">
                    <span className="flex-1">{key}</span>
                    <span className="tabular-nums text-ink-400">{pct}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1 text-xs text-ink-400">
                The print sheet sets the odds — 100 commons to 5 legendaries IS the distribution.
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-ink-400">
            Fixed per item · {Object.keys(reward.rarity.assign).length} assigned
          </p>
        )}
      </Section>
    </>
  );
}

function axisLabel(supply: Reward['supply']): string {
  switch (supply.axis) {
    case 'unlimited': return 'unlimited';
    case 'total': return `${supply.limit.toLocaleString()} in total`;
    case 'per_item': return `${supply.limit.toLocaleString()} of each item`;
    case 'per_item_tier': return 'print sheet';
  }
}

/** Weights are relative, so they are shown as the share they actually produce. */
function shares(weights: Record<string, number>): [string, string][] {
  const total = Object.values(weights).reduce((n, w) => n + Math.max(0, w), 0);
  return Object.entries(weights).map(([key, w]) => [
    key,
    total <= 0 ? '—' : `${String(Math.round((Math.max(0, w) / total) * 100))}%`,
  ]);
}

function BundleDetail({ bundle }: { bundle: Bundle }): ReactNode {
  const packSize = bundle.slots.reduce((n, s) => n + s.count, 0);

  return (
    <>
      <Section title="What one pack holds">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="brand">{packSize} per pack</Badge>
          <Badge>{bundle.slots.length} {bundle.slots.length === 1 ? 'slot' : 'slots'}</Badge>
          <Link href={hrefFor([bundle.channel_id, 'bundles', bundle.id, 'simulate'])}>
            Simulate opens
          </Link>
        </div>
        <p className="mt-2 text-xs text-ink-400">
          Every slot is filled or the pack does not open. A pack is never handed
          over short.
        </p>
      </Section>

      <Section title="Slots">
        <ol className="space-y-3">
          {bundle.slots.map((slot, i) => (
            <li key={i} className="rounded-lg border border-ink-800">
              <div className="flex items-center gap-2 border-b border-ink-800 px-4 py-2 text-sm">
                <span className="tabular-nums text-ink-400">{i + 1}</span>
                <span className="flex-1">{slot.label ?? <span className="text-ink-400">Unlabelled</span>}</span>
                {slot.count > 1 ? <Badge>draws ×{slot.count}</Badge> : null}
                <Badge>{slot.weighting === 'by_remaining' ? 'odds from stock' : 'odds from weights'}</Badge>
              </div>
              <ul className="divide-y divide-ink-800">
                {slot.entries.map((entry) => (
                  <li key={entry.reward_id} className="flex items-center gap-3 px-4 py-2 text-sm">
                    <span className="flex-1 font-mono text-xs">{entry.reward_id}</span>
                    <span className="tabular-nums text-ink-400">
                      {/* Shown as the share it actually produces. A raw weight
                          beside other raw weights is a number nobody can read
                          the odds off. */}
                      {slot.weighting === 'by_remaining' ? 'by stock' : shareOf(slot.entries, entry.reward_id)}
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}

/** One entry's normalised share of its slot. */
function shareOf(entries: Bundle['slots'][number]['entries'], rewardId: string): string {
  const total = entries.reduce((n, e) => n + Math.max(0, e.weight), 0);
  if (total <= 0) return '—';
  const weight = Math.max(0, entries.find((e) => e.reward_id === rewardId)?.weight ?? 0);
  return `${String(Math.round((weight / total) * 100))}%`;
}

function TriggerDetail({ trigger }: { trigger: Trigger }): ReactNode {
  return (
    <>
      <Section title="Fires on">
        <ul className="space-y-2">
          {trigger.matches.map((match, i) => (
            <li key={`${match.type}-${i}`} className="rounded-lg border border-ink-800 px-4 py-3">
              <Badge tone="brand">{match.type}</Badge>
              <Conditions conditions={match.conditions} />
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Pays out">
        <ul className="space-y-1 text-sm">
          {trigger.payouts.currency.map((c) => (
            <li key={c.code}>{c.amount} {c.code}</li>
          ))}
          {trigger.payouts.reward_ids.map((id) => (
            <li key={id} className="font-mono text-xs">{id}</li>
          ))}
          {trigger.payouts.bundle_ids.map((id) => (
            <li key={id} className="font-mono text-xs">{id}</li>
          ))}
        </ul>
      </Section>
    </>
  );
}

/**
 * Conditions, worded rather than dumped.
 *
 * "Absent means do not care" is the rule the matcher follows, and the words
 * have to say so: a tier condition is a FLOOR, not an equality, and a screen
 * that renders `tier: 2000` invites a streamer to expect the opposite.
 */
function Conditions(
  { conditions }: { conditions: Trigger['matches'][number]['conditions'] }
): ReactNode {
  const said: string[] = [];
  if (conditions.tier) said.push(`tier ${conditions.tier} or above`);
  if (conditions.min_months) said.push(`${conditions.min_months}+ months`);
  if (conditions.min_gift_count) said.push(`${conditions.min_gift_count}+ gifted`);
  if (conditions.min_bits) said.push(`${conditions.min_bits}+ bits`);
  if (conditions.min_raid_viewers) said.push(`${conditions.min_raid_viewers}+ raiders`);
  if (conditions.chat_command) said.push(`someone types ${conditions.chat_command}`);
  if (conditions.channel_points_reward_id) said.push('a specific channel-point reward');
  if (conditions.allow_anonymous) said.push('anonymous gifters included');

  return said.length > 0
    ? <p className="mt-2 text-sm text-ink-200">{said.join(', ')}</p>
    : <p className="mt-2 text-sm text-ink-400">Every time, with no further conditions.</p>;
}

function getterFor(kind: CatalogKind) {
  return (channelId: string, id: string, signal: AbortSignal): Promise<Entry> => {
    switch (kind) {
      case 'rewards': return rewards.get(channelId, id, signal);
      case 'bundles': return bundles.get(channelId, id, signal);
      case 'triggers': return triggers.get(channelId, id, signal);
    }
  };
}

function updaterFor(kind: CatalogKind) {
  return (channelId: string, id: string, patch: Record<string, unknown>): Promise<Entry> => {
    switch (kind) {
      case 'rewards': return rewards.update(channelId, id, patch);
      case 'bundles': return bundles.update(channelId, id, patch);
      case 'triggers': return triggers.update(channelId, id, patch);
    }
  };
}
